import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  getMessaging,
  deleteToken,
  getToken,
  isSupported,
  onMessage,
  type Messaging,
  type Unsubscribe,
} from 'firebase/messaging';
import { dispositivoPushService } from './dispositivoPushService';
import {
  getFirebaseVapidKey,
  getFirebaseWebConfig,
  isPushConfigured,
} from './firebaseConfig';
import {
  clearStoredPushToken,
  getStoredPushToken,
  setStoredPushToken,
} from './pushTokenStorage';

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;

function ensureApp(): FirebaseApp | null {
  const config = getFirebaseWebConfig();
  if (!config) return null;
  if (app) return app;
  app = getApps().length > 0 ? getApps()[0]! : initializeApp(config);
  return app;
}

async function ensureMessaging(): Promise<Messaging | null> {
  if (!(await isSupported())) return null;
  const firebaseApp = ensureApp();
  if (!firebaseApp) return null;
  if (!messaging) {
    messaging = getMessaging(firebaseApp);
  }
  return messaging;
}

export async function registerPushToken(): Promise<string | null> {
  if (!isPushConfigured()) {
    console.warn('Push: Firebase nao configurado (VITE_FIREBASE_*)');
    return null;
  }
  if (typeof Notification === 'undefined') {
    console.warn('Push: Notification API indisponivel');
    return null;
  }

  const vapidKey = getFirebaseVapidKey();
  if (!vapidKey) {
    console.warn('Push: VAPID ausente');
    return null;
  }

  const permission =
    Notification.permission === 'granted'
      ? 'granted'
      : await Notification.requestPermission();
  if (permission !== 'granted') {
    console.warn('Push: permissao=', permission);
    return null;
  }

  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
    scope: '/',
  });
  await navigator.serviceWorker.ready;

  const msg = await ensureMessaging();
  if (!msg) {
    console.warn('Push: messaging nao suportado');
    return null;
  }

  const token = await getToken(msg, {
    vapidKey,
    serviceWorkerRegistration: registration,
  });
  if (!token) {
    console.warn('Push: getToken vazio');
    return null;
  }

  await dispositivoPushService.registrar({
    token,
    plataforma: 'WEB',
    aplicativo: 'WEB_ESCOLA',
    versaoApp: import.meta.env.VITE_APP_VERSION ?? undefined,
  });
  setStoredPushToken(token);
  console.info('Push: token registrado');
  return token;
}

export async function desativarPushTokenAtual(): Promise<void> {
  const token = getStoredPushToken();
  try {
    const msg = await ensureMessaging();
    if (msg) await deleteToken(msg);
  } catch {
    /* o token local pode ja ter sumido */
  }
  if (!token) {
    clearStoredPushToken();
    return;
  }
  try {
    await dispositivoPushService.desativar(token);
  } catch {
    /* logout segue mesmo se falhar */
  } finally {
    clearStoredPushToken();
  }
}

/** Aviso recebido com a aba em foco (o SW so trata background). */
export type AvisoForeground = {
  titulo: string;
  corpo: string;
  /** `CHAT_MENSAGEM` ou `PUBLICACAO` (publicacao nova ou comentario). */
  tipo?: string;
  idConversa?: string;
  idPublicacao?: string;
  idEvento?: string;
  dataEvento?: string;
  idComentario?: string;
  idAluno?: string;
  dataPublicacao?: string;
};

export async function subscribeForegroundPush(
  onPayload: (aviso: AvisoForeground) => void,
): Promise<Unsubscribe | null> {
  const msg = await ensureMessaging();
  if (!msg) return null;
  return onMessage(msg, (payload) => {
    const data = payload.data ?? {};
    onPayload({
      titulo: payload.notification?.title ?? data.titulo ?? 'MomyEdu',
      corpo: payload.notification?.body ?? data.corpo ?? '',
      tipo: data.tipo,
      idConversa: data.idConversa,
      idPublicacao: data.idPublicacao,
      idEvento: data.idEvento,
      dataEvento: data.dataEvento,
      idComentario: data.idComentario,
      idAluno: data.idAluno,
      dataPublicacao: data.dataPublicacao,
    });
  });
}
